import { useCallback, useEffect, useState } from 'react';

import { getVideoPolicy, getVideoState, type IResultVideoState, type PolicyOutcome, videoOffered } from '../lib/result-video-api';
import { getVideoRecord, type IResultVideoRecord, subscribeResultVideo } from '../lib/result-video-store';
import { syncResultVideo } from '../lib/result-video-sync';

/**
 * Everything a surface needs about one unit's result video: the policy (is it offered at all),
 * the local outbox record (what this phone is doing), the server state (what reviewers see),
 * and live upload progress.
 */
export const useResultVideo = (
	electionId: number | undefined,
	pollingUnitCode: string | undefined,
	{ fetchServer = true } = {}
) => {
	const [policy, setPolicy] = useState<PolicyOutcome | null>(null);
	const [record, setRecord] = useState<IResultVideoRecord | null>(null);
	const [server, setServer] = useState<IResultVideoState | null>(null);
	const [serverLoading, setServerLoading] = useState(false);
	const [progress, setProgress] = useState<number | undefined>(undefined);

	const ready = !!electionId && !!pollingUnitCode;

	useEffect(() => {
		if (!electionId) return;
		let live = true;
		void getVideoPolicy(electionId).then((p) => live && setPolicy(p));
		return () => {
			live = false;
		};
	}, [electionId]);

	useEffect(() => {
		if (!ready) return;
		const read = () => setRecord(getVideoRecord(electionId, pollingUnitCode));
		read();
		return subscribeResultVideo(electionId, pollingUnitCode, read);
	}, [ready, electionId, pollingUnitCode]);

	const refreshServer = useCallback(async () => {
		if (!ready || !fetchServer) return;
		setServerLoading(true);
		const res = await getVideoState(electionId, pollingUnitCode);
		setServerLoading(false);
		if (res.kind === 'ok') setServer(res.data);
	}, [ready, fetchServer, electionId, pollingUnitCode]);

	const offered = videoOffered(policy);

	useEffect(() => {
		if (offered) void refreshServer();
	}, [offered, refreshServer]);

	// Once this phone's upload registers, the server copy is stale — fetch the new one.
	useEffect(() => {
		if (record?.state === 'registered') void refreshServer();
	}, [record?.state, record?.registeredAt, refreshServer]);

	/** Start (or force) an upload now, reporting progress to this surface. */
	const send = useCallback(
		(force = false) => {
			if (!ready) return;
			setProgress(undefined);
			void syncResultVideo(electionId, pollingUnitCode, { force, onProgress: setProgress }).finally(() => setProgress(undefined));
		},
		[ready, electionId, pollingUnitCode]
	);

	return { policy, offered, record, server, serverLoading, progress, refreshServer, send };
};
